import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import logo from "@images/1ds_logo.jpg";

import {
    addPortalsState,
    getIntegrationsAppId,
    getPortalUrl,
    updateClientIntegration,
} from "@ts/retrieve-data";
import {
    IClientListItem,
    ChannelIntegrationAccountType,
    EnvironmentType,
    TIntegrationPayload,
    EAccountType,
} from "@/ts/common-types";
import { showToast } from "@/ts/modals";
import { Spin, Divider, Button, Form, Collapse } from "antd";
import { FaExchangeAlt } from "react-icons/fa";
import { LeftOutlined } from "@ant-design/icons";
import "@/scss/channel-integration.scss";
import * as Sentry from "@sentry/react";
import ConnectClientChannelIntegrationModal from '@/components/modals/ConnectClientChannelIntegrationModal';
import { CHANNEL_CONFIGS } from '@/config/channel-configs';
import DisconnectIntegrationModal from '@/components/modals/DisconnectIntegrationModal';
import { usePermissionCheck } from '@/hooks/AllCustomHooks';
import { titleCase } from "@/ts/utils";

interface IChannelIntegrationsProps {
    client: IClientListItem;
    channelId: number;
    onBack: () => void;
    isIntegrated: boolean;
    onDisconnect?: () => void;
}

const { Panel } = Collapse;

export const ChannelIntegrations = ({
    client,
    channelId,
    onBack,
    isIntegrated,
    onDisconnect,
}: IChannelIntegrationsProps) => {
    const { canUpdateClient } = usePermissionCheck();
    const [loading, setLoading] = useState(false);
    const [appId, setAppId] = useState("");
    const [states, setStates] = useState<string[]>([]);
    const [avlLinks, setAvlLinks] = useState();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [redirectUri] = useState(process.env.VITE_INTEGRATION_REDIRECT_URL);
    const [form] = Form.useForm();
    const [showDisconnectModal, setShowDisconnectModal] = useState(false);

    const channelConfig = CHANNEL_CONFIGS[channelId];

    useEffect(() => {
        if (channelConfig.oauthEnabled) {
            populateLink();
        }
    }, [channelId]);

    const populateLink = async () => {
        setLoading(true);
        try {
            const authLinks = await onConnect();
            if (!authLinks) {
                throw new Error("Unable to get links!");
            }
            setAvlLinks(authLinks);
        } catch (err: any) {
            console.error(err);
            showToast(
                "danger",
                err.message || "Failed to load integration links"
            );
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    };

    const onConnect = async () => {
        try {
            const appIdResponse = await getIntegrationsAppId();
            if (!appIdResponse) {
                throw new Error(appIdResponse.message);
            }
            setAppId(appIdResponse.amazonAppId);

            const stateUUID = uuidv4();
            const stateArr = [
                `${EAccountType.SELLER}-${stateUUID}`,
                `${EAccountType.VENDOR}-${stateUUID}`,
            ];
            setStates(stateArr);

            const stateResponse = await addPortalsState(stateArr);
            if (!stateResponse) {
                throw new Error(stateResponse.message);
            }

            const urlResponse = await getPortalUrl(
                channelId,
                client.country,
                "NA"
            );

            if (!urlResponse) {
                throw new Error(urlResponse.message);
            }
            return urlResponse;
        } catch (err: any) {
            console.error(err);
            showToast(
                "danger",
                err.message || "Failed to load integration links"
            );
            Sentry.captureException(err);
        }
    };

    const handleOAuthConnect = (type: string) => {
        const baseURL = (avlLinks as Record<string, string>[]).find(
            (link: Record<string, string>) => link.account_type === type
        );

        const stId = states.find((st: string) => st.split("-")[0] === type);

        const params = new URLSearchParams({
            application_id: appId,
            version:
                process.env.VITE_NODE_ENV === EnvironmentType.Local
                    ? EnvironmentType.Beta
                    : "",
            state: stId,
            redirect_uri: redirectUri,
        });

        const linkToRedirect = `${
            baseURL.portal_url
        }/apps/authorize/consent?${params.toString()}`;

        const redirectWindow = window.open(
            linkToRedirect,
            "_blank",
            "height=600,width=600"
        );

        if (redirectWindow) {
            redirectWindow.focus();
        }
    };

    const handleConnect = async () => {
        if (!canUpdateClient) {
            showToast(
                "danger",
                "You do not have permission to perform this action"
            );
            return;
        }

        try {
            setLoading(true);
            const values = await form.validateFields();

            const missingFields = channelConfig.authFields
                .filter(field => field.required && !values[field.id]?.trim())
                .map(field => field.label);
            
            if (missingFields.length > 0) {
                showToast("danger", `Please enter ${missingFields.join(", ")}`);
                return;
            }

            const payload: TIntegrationPayload = {
                platform_status: true,
            };
            
            channelConfig.authFields.forEach(field => {
                if (values[field.id]) {
                    payload[field.id] = values[field.id].trim();
                }
            });

            await updateClientIntegration(client.id, channelId, payload);

            showToast(
                "success",
                `${channelConfig.name} account connected successfully`
            );
            setIsModalVisible(false);
            form.resetFields();
            onDisconnect?.();
        } catch (err: any) {
            console.error(err);
            showToast("danger", err.message || "Failed to connect integration");
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDisconnect = async () => {
        if (!canUpdateClient) {
            showToast(
                "danger",
                "You do not have permission to perform this action"
            );
            return;
        }

        try {
            setLoading(true);
            await updateClientIntegration(client.id, channelId, {
                platform_status: false,
            });
            showToast(
                "success",
                `${channelConfig.name} account disconnected successfully`
            );
            onDisconnect?.();
            onBack();
        } catch (err: any) {
            console.error(err);
            showToast(
                "danger",
                err.message || "Failed to disconnect integration"
            );
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    };

    const renderConnectAccount = () => {
        const enabledAccountTypes = channelConfig.accountTypes.filter(type => type.enabled);
        
        const renderConnectionButtons = () => {
            return enabledAccountTypes.map((type, index, array) => (
                <React.Fragment key={type.type}>
                    <div className={!canUpdateClient ? 'cursor-not-allowed' : ''}>
                        <button
                            className={`btn ${isIntegrated ? 'btn-danger' : 'btn-primary'} connect-channel-btn`}
                            onClick={() => {
                                if (isIntegrated) {
                                    setShowDisconnectModal(true);
                                } else {
                                    if (type.type === ChannelIntegrationAccountType.Advertising) {
                                        setIsModalVisible(true);
                                    } else if (channelConfig.oauthEnabled) {
                                        handleOAuthConnect(type.type);
                                    } else {
                                        setIsModalVisible(true);
                                    }
                                }
                            }}
                            disabled={!canUpdateClient}
                            title={!canUpdateClient ? "You don't have permission to perform this action" : ""}
                        >
                            {isIntegrated 
                                ? `Disconnect ${titleCase(type.type)}` 
                                : `Connect ${titleCase(type.type)}`
                            }
                        </button>
                    </div>
                    {index < array.length - 1 && (
                        <Divider type="vertical" className="connect-channel-btn-divider" />
                    )}
                </React.Fragment>
            ));
        };

        return (
            <div className="integrations-connect-container">
                <div style={{ textAlign: "center", padding: "25px 0 20px 0" }}>
                    <img
                        className="integrations-connect-logo"
                        src={logo}
                        alt="1ds-logo"
                    />
                    <FaExchangeAlt
                        style={{
                            fontSize: "50px",
                            color: "gray",
                            margin: "auto 20px",
                        }}
                    />
                    {channelConfig.logo &&
                        React.cloneElement(channelConfig.logo, {
                            className: "integrations-connect-logo",
                        })}
                </div>
                <div className="d-flex justify-content-center align-items-center my-3">
                    {enabledAccountTypes.length > 0 ? (
                        renderConnectionButtons()
                    ) : (
                        <div
                            className={
                                !canUpdateClient ? "cursor-not-allowed" : ""
                            }
                        >
                            <button
                                className={`btn ${
                                    isIntegrated ? "btn-danger" : "btn-primary"
                                } connect-channel-btn`}
                                onClick={() =>
                                    isIntegrated
                                        ? setShowDisconnectModal(true)
                                        : setIsModalVisible(true)
                                }
                                disabled={!canUpdateClient}
                                title={
                                    !canUpdateClient
                                        ? "You don't have permission to perform this action"
                                        : ""
                                }
                            >
                                {isIntegrated ? 'Disconnect' : 'Connect'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderReviewIntegration = () => {
        return (
            <div className="review-integration-container p-4">
                <h4>Integration Status</h4>
                {/* To be added integration status review content here */}
                <div className="alert alert-info mt-3">
                    Integration status and details will be displayed here
                </div>
            </div>
        );
    };

    if (!channelConfig) {
        return <div>Invalid channel ID</div>;
    }

    if (loading) {
        return (
            <div className="text-center p-4">
                <Spin />
            </div>
        );
    }

    return (
        <div>
            <Button
                onClick={onBack}
                className="mb-3 d-flex align-items-center"
                style={{ gap: "4px" }}
            >
                <LeftOutlined /> Back to Integrations
            </Button>
            <div className="channel-integration-container">
                <div className="d-flex align-items-center p-3">
                    {React.cloneElement(channelConfig.logo, {
                        width: "3.5rem",
                        height: "3.5rem",
                    })}
                    <h3 className="mx-3 fw-bolder">
                        Integrate with {channelConfig.name}
                    </h3>
                </div>

                <Collapse
                    defaultActiveKey={["connect"]}
                    className="integration-collapse"
                    expandIconPosition="end"
                >
                    <Panel
                        header="Integration Overview"
                        key="overview"
                        collapsible="disabled"
                        className="integration-panel"
                    >
                        {/* Content will be added later */}
                    </Panel>
                    <Panel
                        header="Connect Account"
                        key="connect"
                        className="integration-panel"
                    >
                        {renderConnectAccount()}
                    </Panel>
                    <Panel
                        header="Review Integration"
                        key="review"
                        className="integration-panel"
                    >
                        {renderReviewIntegration()}
                    </Panel>
                </Collapse>
            </div>

            <ConnectClientChannelIntegrationModal
                isOpen={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onSubmit={handleConnect}
                loading={loading}
                channelName={channelConfig.name}
                channelLogo={channelConfig.logo}
                form={form}
                channelConfig={channelConfig}
            />

            <DisconnectIntegrationModal
                isOpen={showDisconnectModal}
                onCancel={() => setShowDisconnectModal(false)}
                onConfirm={handleDisconnect}
            />
        </div>
    );
};
